import React from 'react';
import { Box, Grid, Paper, Stack, Typography } from '@mui/material';

import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';

function stepCard(num: number, title: string, text: string, detail?: string) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{
        minWidth: 38, height: 38, borderRadius: '50%',
        bgcolor: TREND_COLORS.gold, color: '#fff', fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: DISPLAY_FONT, fontSize: '1rem', flexShrink: 0, mt: 0.3,
      }}>
        {num}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1rem', color: TREND_COLORS.navy, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.65, mt: 0.3 }}>
          {text}
        </Typography>
        {detail && (
          <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block', mt: 0.5, lineHeight: 1.5, fontStyle: 'italic' }}>
            {detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function GuideTab() {
  return (
    <Stack spacing={4} sx={{ py: 2 }}>
      <Box>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: TREND_COLORS.navy }}>
          Så fungerar trendmodulen
        </Typography>
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 1, maxWidth: 700, lineHeight: 1.7 }}>
          Trendmodulen är Fontanas egna AI-drivna omvärldsbevakning. Den hämtar automatiskt nyheter, bloggar och branschrapporter
          från hela världen — analyserar dem med artificiell intelligens — och levererar färdiga, relevanta trendinsikter
          anpassade efter Fontanas verklighet. Här förklarar vi alla steg.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 0.5 }}>
          Kontrollcentrets ytor
        </Typography>
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.6, mb: 2.5 }}>
          Kontrollcentret är indelat i tre delar: <strong>Drift</strong> (daglig körning),
          <strong> Konfiguration</strong> (hur modulen arbetar) och <strong> Insyn &amp; arkiv</strong> (utforska resultatet).
        </Typography>
        <Grid container spacing={2}>
          {[
            { group: 'Drift', name: 'Status & Körning', text: 'Starta körningar och följ status och driftloggar för pipelinen.' },
            { group: 'Drift', name: 'Discovery', text: 'AI-webbsök och riktad domäncrawl som breddar bevakningen utöver RSS.' },
            { group: 'Drift', name: 'Brand Radar', text: 'Bevaka omnämnanden av Fontana och konkurrenter i källflödet.' },
            { group: 'Konfiguration', name: 'Källor', text: 'Lägg till och hantera RSS-källor samt deras vikt i körningen.' },
            { group: 'Konfiguration', name: 'Inställningar & Profil', text: 'Körningsgränser, källstrategi och Fontana-profil som styr AI:n.' },
            { group: 'Insyn & arkiv', name: 'Hämtat material', text: 'Bläddra i de råa källposter och artiklar som har hämtats.' },
            { group: 'Insyn & arkiv', name: 'Historik', text: 'Se hur enskilda trendsignaler utvecklas över tid – styrka och status.' },
            { group: 'Insyn & arkiv', name: 'Rapportarkiv', text: 'Färdiga, publicerade trendrapporter från tidigare körningar.' },
          ].map((area) => (
            <Grid key={area.name} size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: 2, bgcolor: TREND_COLORS.softNavy, borderRadius: 2, height: '100%' }}>
                <Typography variant="caption" sx={{ color: TREND_COLORS.gold, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.6rem' }}>
                  {area.group}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>{area.name}</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block', mt: 0.25 }}>
                  {area.text}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${TREND_COLORS.divider}` }}>
        <Box
          component="img"
          src="/images/trends/trend-pipeline-flow.png"
          alt="Trendmodulens pipeline-flöde"
          sx={{ width: '100%', display: 'block' }}
        />
      </Paper>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 3 }}>
          Huvudpipelinen — steg för steg
        </Typography>
        <Stack spacing={2.5}>
          {stepCard(1, 'Inställningar',
            'Allt börjar med dina inställningar i kontrollcentret. Här bestämmer du hur ofta systemet ska köra, vilka källor som ska bevakas, vilka teman som är viktigast, och hur djupt AI:n ska analysera.',
            'Du styr alltifrån antalet källor till om sociala medier ska bevakas. Varje inställning påverkar vad du ser på trendsidorna.'
          )}
          {stepCard(2, 'Källhämtning (RSS-flöden)',
            'Systemet hämtar automatiskt nya artiklar från alla aktiverade RSS-källor — matbloggar, branschtidningar, nyhetssajter och foodservice-publikationer. Varje källa kan leverera upp till det antal artiklar du ställt in.',
            'Tänk på det som att ha en digital praktikant som varje morgon läser igenom hundratals tidningar åt dig.'
          )}
          {stepCard(3, 'AI-analys och trendidentifiering',
            'Varje hämtad artikel skickas till OpenAI:s AI-modell. AI:n läser artikeln, identifierar relevanta trender och kategoriserar dem: mat & recept, design & förpackning, marknad & konkurrens, eller kundbeteenden. Den bedömer också hur stark och relevant varje trend är för Fontana.',
            'AI:n har fått detaljerad information om Fontanas produkter, positionering och mål — så den vet vad som är relevant.'
          )}
          {stepCard(4, 'Verifiering — en extra AI-kontroll',
            'De mest intressanta trenderna verifieras med ett separat AI-anrop. Här kontrolleras att trenden verkligen stämmer, att den inte är en engångsföreteelse, och att den har substans. Trender som inte klarar verifieringen filtreras bort.',
            'Det här steget är som en redaktör som dubbelkollar fakta innan publicering.'
          )}
          {stepCard(5, 'Produktmatchning',
            'Nu matchas varje verifierad trend mot Fontanas produktsortiment. AI:n föreslår vilka av Fontanas produkter (fetaost, halloumi, grilloumi, olivolja, oliver, yoghurt, röror) som kopplar till trenden och hur.',
            'Det här är det mest resurskrävande steget — varje matchning kräver flera AI-anrop för att bli riktigt bra.'
          )}
          {stepCard(6, 'Bildhantering',
            'Systemet hämtar automatiskt huvudbilden från varje artikel genom att analysera sidans metadata: Open Graph-bilder, Twitter-bilder och bildelement i HTML:en. Om ingen bild hittas används en kategorispecifik reservbild.',
            'Bilderna gör att trendsidorna känns visuella och levande istället för textunga.'
          )}
          {stepCard(7, 'Berättelsekluster',
            'Relaterade trender och artiklar grupperas ihop till sammanhängande berättelser kring ett tema. Om fem olika källor skriver om samma fenomen blir det en enda, stark berättelse istället för fem separata trender.',
            'Klustring gör att översiktssidan visar de viktigaste berättelserna, inte enskilda artiklar.'
          )}
          {stepCard(8, 'Affärsmöjligheter (Gap-analys)',
            'AI:n analyserar alla trender och jämför med Fontanas nuvarande sortiment och position. Den identifierar luckor och möjligheter: "Här finns en stark trend som Fontana ännu inte adresserar" eller "Denna trend passar perfekt för en kampanj med halloumi".',
            'Resultatet visas på sidan "Affärsmöjligheter" och kopplar ihop trendinformation med konkreta handlingsförslag.'
          )}
          {stepCard(9, 'Artikelgenerering och publicering',
            'Alla analyserade och verifierade artiklar, berättelser och möjligheter publiceras på trendsidorna. Artiklarna får svenska rubriker, ingresser och kategorier. Du som redaktör kan sedan lyfta fram artiklar, välja huvudartikel på översikten, och dölja irrelevant innehåll via redaktionellt-sidan.',
            'Tänk på det som en färdig tidning som levereras till dig — men där du kan byta ut förstasidan.'
          )}
          {stepCard(10, 'Färdigt — trendsidorna uppdateras',
            'När pipelinen är klar uppdateras alla sidor automatiskt: Översikt, Mat & Recept, Design & Förpackning, Marknad & Konkurrens och Affärsmöjligheter. Allt baserat på färsk data, AI-analys och Fontanas egna profil.',
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden', border: `1px solid ${TREND_COLORS.divider}` }}>
        <Box
          component="img"
          src="/images/trends/trend-discovery-recipe-flow.png"
          alt="Discovery och receptanpassning"
          sx={{ width: '100%', display: 'block' }}
        />
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3, height: '100%' }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 2 }}>
              Discovery med hybridlager
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 2 }}>
              Utöver RSS-källorna kan du aktivera <strong>Discovery</strong> - en kombination av AI-webbsök och riktad domäncrawl som breddar täckningen utan att låtsas vara en crawl av hela webben.
            </Typography>
            <Stack spacing={1.5}>
              {stepCard(1, 'Du definierar sökfrågor',
                'I Discovery-tabben skriver du seed-frågor som systemet ska undersöka, t.ex. "Nya recepttrender med fetaost 2026" eller "Förpackningsdesign premium mejeriprodukter".'
              )}
              {stepCard(2, 'AI-webbsök expanderas',
                'Systemet expanderar frågorna deterministiskt efter språk, marknad och fokusläns och samlar kandidatartiklar från öppna källor. Varje kandidat betygsätts efter relevans för Fontana.'
              )}
              {stepCard(3, 'Riktad crawl kan adderas',
                'För utvalda sajter kan du lägga till sitemap- eller kategorisidescrawl som hittar nya artikel-URL:er utan att du behöver bygga en generell crawler.'
              )}
              {stepCard(4, 'Du granskar och godkänner',
                'Kandidaterna visas i Discovery-tabben. Du kan godkänna, avvisa eller sätta dem i karantän. Godkända kandidater kan importeras.'
              )}
              {stepCard(5, 'Import till artiklar',
                'Godkända kandidater hämtas, analyseras och blir fullvärdiga trendartiklar med bilder, kategorier och AI-analys — precis som RSS-artiklar.'
              )}
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3, height: '100%' }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 2 }}>
              Receptanpassning med AI
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 2 }}>
              När en trendande artikel handlar om ett viralt recept kan du med ett knapptryck skapa ett
              <strong> Fontana-anpassat recept</strong>.
            </Typography>
            <Stack spacing={1.5}>
              {stepCard(1, 'Viral trend upptäcks',
                'Systemet identifierar artiklar som handlar om trendande recept — exempelvis en viral TikTok-sallad eller en ny matlagningsmetod.'
              )}
              {stepCard(2, 'AI analyserar receptet',
                'Artikelns recept skickas till AI:n som förstår ingredienser, tillagningsmetod och smakprofil.'
              )}
              {stepCard(3, 'Fontana-produkter matchas in',
                'AI:n byter ut eller kompletterar ingredienser med Fontanas produkter — t.ex. vanlig fetaost blir Fontana Fetaost, eller olivolja blir Fontana Extra Virgin.'
              )}
              {stepCard(4, 'Komplett recept genereras',
                'Resultatet är ett färdigt recept med ingredienslista (där Fontana-produkter är markerade), steg-för-steg-instruktioner, serveringstips och portionsantal.'
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.softNavy, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 2 }}>
          Redaktionell kontroll
        </Typography>
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 2 }}>
          Trots att systemet är automatiserat har du som användare full kontroll:
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: TREND_COLORS.cardBg, borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>Lyft fram artiklar</Typography>
              <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                Markera artiklar som "framlyft" så hamnar de överst. Välj huvudartikel för översiktssidans toppbanner.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: TREND_COLORS.cardBg, borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>Pinna och dölj</Typography>
              <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                Pinna specifika artiklar och berättelser till "Präglar veckan" och "Toppberättelser". Dölj innehåll som inte är relevant.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ p: 2, bgcolor: TREND_COLORS.cardBg, borderRadius: 2, height: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>Skapa recept</Typography>
              <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                Klicka på en receptartikel och generera ett Fontana-anpassat recept direkt från detaljvyn.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mb: 2 }}>
          AI-teknik under huven
        </Typography>
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.7, mb: 2 }}>
          Trendmodulen använder avancerad AI-teknik i varje steg. Här är en översikt av vad som händer bakom kulisserna:
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>OpenAI GPT-4o</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>Analyserar artiklar, identifierar trender, verifierar, matchar produkter och genererar recept.</Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>JSON-strukturerade svar</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>All AI-kommunikation använder strukturerad JSON för att garantera konsistent data.</Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Fontana-kontext i varje prompt</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>AI:n har alltid tillgång till din Fontana-profil så att analysen anpassas efter era produkter och mål.</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Semantisk klustring</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>Relaterade artiklar grupperas baserat på innehållets betydelse, inte bara nyckelord.</Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Automatisk bildextraktion</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>Hämtar OG-images, Twitter-cards och HTML-bilder automatiskt från varje källa.</Typography>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: TREND_COLORS.softNavy, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Evidensmodell</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, display: 'block' }}>Varje trend har en evidenspoäng baserad på antal oberoende källor, domänspridning och färskhet.</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ p: 3, bgcolor: TREND_COLORS.navy, borderRadius: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: TREND_COLORS.gold, mb: 1 }}>
          Sammanfattning
        </Typography>
        <Typography variant="body2" sx={{ color: '#ffffffcc', lineHeight: 1.8, maxWidth: 700, mx: 'auto' }}>
          Trendmodulen kombinerar kuraterade källor, AI-webbsök i öppna källor, riktad domäncrawl
          för utvalda sajter och redaktionell kontroll. Resultatet är en levande trendsida som hjälper
          Fontana att ligga steget före med insikter som är skräddarsydda för era produkter, er marknad
          och era mål. Modulen gör inte en full crawl av hela webben, men den visar nu tydligare vilken
          täckning varje discoverykörning faktiskt gav.
        </Typography>
      </Box>
    </Stack>
  );
}
